import { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Button, Card, CardContent, Option, Select } from "@deere/fuel-react";
import { Box } from "@mui/material";
import { useTranslation } from "../../common/translation-context";
import { IB_CALCULATION_DOMAINDATA } from "../../constants/constant";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { getDataWAuth } from "../../utils/api-service";

const IBFilters = (props) => {
  const history = useHistory();
  const location = useLocation();
  const path = history.location.pathname;
  const ibConditionCalculation = path.includes("calculationsPage");
  const { translate: t } = useTranslation();
  const [listOfCountries, setListOfCountries] = useState([]);
  const [listOfYears, setListOfYears] = useState([]);
  const [fiscalMonths, setFiscalMonths] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [selectedFiscalYear, setSelectedFiscalYear] = useState("");
  const [selectedFiscalMonthKey, setSelectedFiscalMonthKey] = useState("");
  const [selectedFiscalMonth, setSelectedFiscalMonth] = useState("");
  const [disableSearchButton, setDisableSearchButton] = useState(true);

  const {
    handleReportCountryChange,
    selectedReportCountry,
    reportCountryList,
  } = props;

  const updateSessionStorage = () => {
    const defaultSelection = {
      countryCode: selectedCountryCode,
      countryName: selectedCountry,
      fiscalYear: selectedFiscalYear,
      fiscalMonthKey: selectedFiscalMonthKey,
      fiscalMonth: selectedFiscalMonth,
      associatedMonths: fiscalMonths,
    };
    sessionStorage.setItem(
      "defaultSelection",
      JSON.stringify(defaultSelection),
    );
  };

  const setDefaultValues = (response) => {
    const { defaultCountry, defaultFiscalYear, defaultFiscalMonth } =
      response.data;
    setFiscalMonths(defaultFiscalYear.associatedMonths);
    setSelectedFiscalYear(defaultFiscalYear.fiscalYear);
    setSelectedFiscalMonthKey(Object.keys(defaultFiscalMonth)[0]);
    setSelectedFiscalMonth(Object.values(defaultFiscalMonth));
    setSelectedCountry(defaultCountry.countryName);
    setSelectedCountryCode(defaultCountry.countryCode);

    const defaultSelection = {
      countryCode: defaultCountry.countryCode,
      countryName: defaultCountry.countryName,
      fiscalYear: defaultFiscalYear.fiscalYear,
      fiscalMonthKey: Object.keys(defaultFiscalMonth)[0],
      fiscalMonth: Object.values(defaultFiscalMonth),
      associatedMonths: defaultFiscalYear.associatedMonths,
    };
    sessionStorage.setItem(
      "defaultSelection",
      JSON.stringify(defaultSelection),
    );
    props.getSelectedFilters(
      defaultCountry.countryCode,
      Object.keys(defaultFiscalMonth)[0],
    );

    // If redirected from dashboard with country in location.state, override country selection
    const countryNameFromState = location?.state?.countryName;
    const countryCodeFromState = location?.state?.countryCode || location?.state?.country;
    const fiscalMonthKeyFromState = location?.state?.fiscalMonthKey;
    
    if (countryNameFromState || countryCodeFromState) {
      const match = response.data.countryList.find(
        (c) => c.countryCode === countryCodeFromState || c.countryName === countryNameFromState
      );
      
      if (match) {
        setSelectedCountry(match.countryName);
        setSelectedCountryCode(match.countryCode);
        
        // Determine which fiscal month to use
        let fiscalMonthKeyToUse = Object.keys(defaultFiscalMonth)[0];
        let fiscalMonthValueToUse = Object.values(defaultFiscalMonth);
        if (fiscalMonthKeyFromState) {
          const matchingMonth = defaultFiscalYear.associatedMonths.find(
            (month) => Object.keys(month)[0] === fiscalMonthKeyFromState
          );
          
          if (matchingMonth) {
            fiscalMonthKeyToUse = Object.keys(matchingMonth)[0];
            fiscalMonthValueToUse = Object.values(matchingMonth);
            setSelectedFiscalMonthKey(fiscalMonthKeyToUse);
            setSelectedFiscalMonth(fiscalMonthValueToUse);
          }
        }
        
        // Update defaultSelection with overridden country and fiscal month
        const overriddenSelection = {
          countryCode: match.countryCode,
          countryName: match.countryName,
          fiscalYear: defaultFiscalYear.fiscalYear,
          fiscalMonthKey: fiscalMonthKeyToUse,
          fiscalMonth: fiscalMonthValueToUse,
          associatedMonths: defaultFiscalYear.associatedMonths,
        };
        
        sessionStorage.setItem("defaultSelection", JSON.stringify(overriddenSelection));
        // Trigger filter application with redirected country and fiscal month
        props.getSelectedFilters(match.countryCode, fiscalMonthKeyToUse);
        // Enable search button state appropriately
        setDisableSearchButton(true);
      }
    }
  };

  const getDomainData = () => {
    getDataWAuth(IB_CALCULATION_DOMAINDATA)
      .then((response) => {
        if (response.status === 200) {
          if (response.data.calculationSchedulerStatus === true) {
            props.isSchedulerRunning(true);
          }
          setListOfCountries(response.data.countryList);
          setListOfYears(response.data.fiscalYearDTOList);
          setDefaultValues(response);
        } else {
          setListOfCountries([]);
          setListOfYears([]);
          setFiscalMonths([]);
        }
      })
      .catch((error) => {
        props.showToast && props.showToast(t("somethingWentWrong"), "error");
      });
  };

  useEffect(() => {
    if (!props.bankReport) {
      getDomainData();
    }
  }, []);

  const SearchFilterButton = (
    selectedCty,
    selectedFisMonth,
    selectedFisYear,
  ) => {
    const defaultSelection = sessionStorage.getItem("defaultSelection");
    if (defaultSelection) {
      const parsedSelection = JSON.parse(defaultSelection);
      const prevCountry = parsedSelection.countryName;
      const prevFiscalMonth = parsedSelection.fiscalMonth[0];
      const prevFiscalYear = parsedSelection.fiscalYear;
      setDisableSearchButton(
        prevCountry === selectedCty &&
          prevFiscalMonth === selectedFisMonth &&
          prevFiscalYear === selectedFisYear,
      );
    }
  };

  const handleCountryChange = (e) => {
    const countryName = e.target.value;
    const selectedCountry = listOfCountries.find(
      (country) => country.countryName === countryName,
    );
    setSelectedCountry(selectedCountry.countryName);
    setSelectedCountryCode(selectedCountry.countryCode);
    SearchFilterButton(
      selectedCountry.countryName,
      selectedFiscalMonth[0],
      selectedFiscalYear,
    );
  };

  const handleFiscalYearChange = (e) => {
    const fiscalYear = e.target.value;
    setSelectedFiscalYear(fiscalYear);
    const selectedYear = listOfYears.find(
      (year) => year.fiscalYear === fiscalYear,
    );
    setFiscalMonths(selectedYear?.associatedMonths || []);
    SearchFilterButton(selectedCountry, selectedFiscalMonth[0], fiscalYear);
  };

  const handleMonthChange = (e) => {
    const selectedMonth = e.target.value;
    const selectedMonthObj = fiscalMonths.find(
      (month) => Object.values(month)[0] === selectedMonth,
    );
    setSelectedFiscalMonth(Object.values(selectedMonthObj));
    const selectedFiscalMonthKey = Object.keys(selectedMonthObj)[0];
    setSelectedFiscalMonthKey(selectedFiscalMonthKey);
    SearchFilterButton(
      selectedCountry,
      Object.values(selectedMonthObj)[0],
      selectedFiscalYear,
    );
  };

  const countryOptions = useMemo(
    () =>
      listOfCountries
        .slice()
        .sort((a, b) => a.countryName.localeCompare(b.countryName))
        .map((item) => item.countryName),
    [listOfCountries],
  );

  const fiscalYearOptions = useMemo(
    () => listOfYears.map((item) => item.fiscalYear),
    [listOfYears],
  );
  const fiscalMonthOptions = useMemo(
    () => fiscalMonths.map((item) => Object.values(item)[0]),
    [fiscalMonths],
  );

  const countryType = () => (
    <div style={{ display: "flex", flexDirection: "column", width: "20rem" }}>
      <label>{t("country")}</label>
      <Select
        onChange={handleCountryChange}
        value={[selectedCountry]}
        options={countryOptions}
      >
        {countryOptions.map((item) => (
          <Option key={item} value={item}>
            {item}
          </Option>
        ))}
      </Select>
    </div>
  );

  const reportCountry = () => (
    <div style={{ display: "flex", flexDirection: "column", width: "20rem" }}>
      <label>{t("country")}</label>
      <Select
        onChange={handleReportCountryChange}
        value={selectedReportCountry}
        multiple="true"
        style={{ width: "200px", height: "120px" }}
      >
        {reportCountryList.map((item) => (
          <Option key={item.countryCode} value={item.countryCode}>
            {item.countryName}
          </Option>
        ))}
      </Select>
    </div>
  );

  const fiscalYearType = () => (
    <div style={{ display: "flex", flexDirection: "column", width: "20rem" }}>
      <label>{t("fiscalYear")}</label>
      <Select
        onChange={handleFiscalYearChange}
        value={[selectedFiscalYear]}
        options={{ fiscalYearOptions }}
      >
        {fiscalYearOptions.map((year) => (
          <Option key={year} value={year}>
            {year}
          </Option>
        ))}
      </Select>
    </div>
  );

  const fiscalMonthsType = () => (
    <div style={{ display: "flex", flexDirection: "column", width: "20rem" }}>
      <label>{t("fiscalMonth")}</label>
      <Select
        onChange={handleMonthChange}
        value={[selectedFiscalMonth[0]]}
        options={{ fiscalMonthOptions }}
      >
        {fiscalMonthOptions.map((month) => (
          <Option key={month} value={month}>
            {month}
          </Option>
        ))}
      </Select>
    </div>
  );

  const submitFilters = () => {
    updateSessionStorage();
    props.getSelectedFilters(selectedCountryCode, selectedFiscalMonthKey);
    setDisableSearchButton(true);
  };

  const uniqueYears = [
    ...new Set(
      (props.dropdownOptions || []).map(
        (option) => option.fismonthyear.split("-")[1],
      ),
    ),
  ];

  const uniqueMonths = [
    ...new Set(
      (props.dropdownOptions || []).map(
        (option) => option.fismonthyear.split("-")[0],
      ),
    ),
  ].sort((a, b) => a.localeCompare(b));

  const fiscalYear = () => (
    <div style={{ display: "flex", flexDirection: "column", width: "20rem" }}>
      <label>{"Fiscal Year"}</label>
      <Select
        id="defaultYear"
        value={props.defaultYear}
        onChange={props.handleYearChange}
        label="Start Fiscal Month"
      >
        {uniqueYears.map((year, index) => (
          <Option key={index} value={year}>
            {year}
          </Option>
        ))}
      </Select>
    </div>
  );

  const startFisMonth = () => (
    <div style={{ display: "flex", flexDirection: "column", width: "20rem" }}>
      <label>{"Start Month"}</label>
      <Select
        id="startFiscalMonth"
        value={props.startFiscalMonth}
        onChange={props.handleStartDateChange}
        label="Start Fiscal Month"
      >
        {uniqueMonths.map((year, index) => (
          <Option key={index} value={year}>
            {year}
          </Option>
        ))}
      </Select>
    </div>
  );

  const endFisMonth = () => (
    <div style={{ display: "flex", flexDirection: "column", width: "20rem" }}>
      <label>{"End Month"}</label>
      <Select
        id="endFiscalMonth"
        value={props.endFiscalMonth}
        onChange={props.handleEndDateChange}
        label="End Fiscal Month"
      >
        {uniqueMonths.map((year, index) => (
          <Option key={index} value={year}>
            {year}
          </Option>
        ))}
      </Select>
    </div>
  );

  const filterHeader = () => {
    if (props.bankReport) {
      return "Standard Rates Per Month";
    } else if (props.dealerReport) {
      return "Dealer Rates Allocation Per Month";
    } else {
      return "Filters";
    }
  };

  return (
    <>
      <Box>
        <Card>
          <CardContent>
            <h3 style={{ marginBottom: "1%" }}>{filterHeader()}</h3>

            <div className="action-area">
              {ibConditionCalculation && countryType()}
              {ibConditionCalculation && fiscalMonthsType()}
              {ibConditionCalculation && fiscalYearType()}
              {props.dealerReport && reportCountry()}
              {(props.bankReport || props.dealerReport) && fiscalYear()}
              {(props.bankReport || props.dealerReport) && startFisMonth()}
              {(props.bankReport || props.dealerReport) && endFisMonth()}
              {(props.bankReport || props.dealerReport) && (
                <Button
                  variant="primary"
                  onClick={props.fetchBankRatesByFiscalMonth}
                  disabled={!props.isViewReportActive}
                  style={{
                    backgroundColor: !props.isViewReportActive
                      ? "gray"
                      : "#ffde00",
                  }}
                >
                  {"View Report"}
                </Button>
              )}
              {ibConditionCalculation && (
                <Button
                  variant="primary"
                  onClick={submitFilters}
                  disabled={disableSearchButton}
                  style={{
                    backgroundColor: !disableSearchButton ? "#ffde00" : "gray",
                  }}
                >
                  {t("search")}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default IBFilters;
